import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Footer from './components/Footer';
import PasswordModal from './components/PasswordModal';
import AIProjectCard from './components/AIProjectCard';
import ProjectFeature, { ProjectSummary } from './components/ProjectFeature';
import Reveal from './components/Reveal';
import './App.css';

// VetVisitBill's card is hidden from AI Projects for now.
const SHOW_VETVISITBILL = false;

// How long the mobile header's section label takes to fade out, and then in,
// and how long the header stays empty in between.
const NAV_LABEL_FADE_MS = 200;
const NAV_LABEL_GAP_MS = 150;

// A page load reports "reload" for its whole lifetime, so remember once it's
// been handled; coming back to the homepage from a case study later in the
// same visit should still return to the visitor's earlier position.
let handledReload = false;

// An unframed, full-size screenshot, used by the VetVisitBill card.
const ProductShot = ({ src, alt }) => (
  <img src={src} alt={alt} className="w-full h-auto block" />
);

function App() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [navLabel, setNavLabel] = useState('home');
  const [navLabelVisible, setNavLabelVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [passwordModal, setPasswordModal] = useState({ isOpen: false, projectName: '', pdfUrl: '' });

  // Refs for sections
  const homeRef = useRef(null);
  const herbaRef = useRef(null);
  const aiProjectsRef = useRef(null);
  const cultureAmpRef = useRef(null);
  const discoRef = useRef(null);
  const olapicRef = useRef(null);
  const flokRef = useRef(null);
  const whosayRef = useRef(null);
  const otherWorkRef = useRef(null);
  const contactRef = useRef(null);

  // Contact form state
  const form = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  // Handle case study view. `target` is now an internal /work/:slug route
  // for the projects that have a dedicated case study page; it can still be
  // a .pdf path for anything not yet migrated off the PDF flow.
  const handleViewCaseStudy = (projectName, target) => {
    rememberHomePosition();
    // Check if already authenticated
    if (sessionStorage.getItem('caseStudyAuth') === 'true') {
      if (target.endsWith('.pdf')) {
        // Use different zoom for problematic PDFs
        const zoomParam = (target.includes('Herba') || target.includes('Disco') || target.includes('Culture Amp')) ? '#zoom=width-fit' : '#zoom=page-fit';
        window.open(target + zoomParam, '_blank');
      } else {
        navigate(target);
      }
    } else {
      setPasswordModal({ isOpen: true, projectName, pdfUrl: target });
    }
  };

  const handlePasswordSuccess = () => {
    const target = passwordModal.pdfUrl;
    if (target.endsWith('.pdf')) {
      // Use different zoom for problematic PDFs
      const zoomParam = (target.includes('Herba') || target.includes('Disco') || target.includes('Culture Amp')) ? '#zoom=width-fit' : '#zoom=page-fit';
      window.open(target + zoomParam, '_blank');
    } else {
      navigate(target);
    }
    setPasswordModal({ isOpen: false, projectName: '', pdfUrl: '' });
  };

  // Handle protected document view (resume/portfolio)
  const handleViewProtectedDocument = (docName, pdfUrl) => {
    if (sessionStorage.getItem('caseStudyAuth') === 'true') {
      window.open(pdfUrl, '_blank');
    } else {
      setPasswordModal({ isOpen: true, projectName: docName, pdfUrl });
    }
  };

  // Handle contact scroll from password modal
  const handleContactScroll = () => {
    setPasswordModal({ isOpen: false, projectName: '', pdfUrl: '' });
    scrollToSection(contactRef);
  };

  // Navigation items
  const navItems = [
    { id: 'home', label: 'Kacey Fillingim', ref: homeRef },
    { id: 'aiProjects', label: 'AI Projects', ref: aiProjectsRef },
    { id: 'herba', label: 'Herba', ref: herbaRef },
    { id: 'cultureAmp', label: 'Culture Amp', ref: cultureAmpRef },
    { id: 'disco', label: 'Disco', ref: discoRef },
    { id: 'olapic', label: 'Olapic', ref: olapicRef },
    { id: 'flok', label: 'Flok', ref: flokRef },
    { id: 'whosay', label: 'WhoSay', ref: whosayRef },
    { id: 'otherWork', label: 'Other Work', ref: otherWorkRef },
    { id: 'contact', label: 'Contact', ref: contactRef },
  ];

  // Scroll to section
  const scrollToSection = (sectionRef) => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  // Remember where the visitor was before opening a case study, as the
  // section they were in plus how far into it, so returning to the homepage
  // (the case study's close button, Back to Home, or the browser's back
  // button) puts them back in the same spot even if image loading shifts the
  // page. Stored for the session and consumed on the next homepage visit.
  const rememberHomePosition = () => {
    // The last section whose top has scrolled past the top of the screen.
    const current = navItems
      .map((item) => item.ref.current)
      .filter((el) => el && el.getBoundingClientRect().top <= 1)
      .pop();
    const saved = current
      ? { id: current.id, offset: -current.getBoundingClientRect().top }
      : { id: null, offset: window.scrollY };
    try {
      sessionStorage.setItem('homeReturn', JSON.stringify(saved));
    } catch {
      // Storage unavailable: the visitor just returns to the top.
    }
  };

  // On arrival, scroll to wherever the visitor is headed:
  // - /#contact (e.g. "Contact Me" in a case study's password modal) or
  //   /#contact-form (the footer's Contact link), or
  // - their earlier position, when returning from a case study.
  // Images and videos above haven't loaded yet and keep pushing the target
  // down, so re-align on every layout change until the page settles or the
  // visitor scrolls on their own.
  // A refresh always starts at the top instead: the browser's own scroll
  // restoration is turned off, and a leftover #contact / #contact-form hash
  // is dropped so the page doesn't jump back down to the form.
  useEffect(() => {
    const [navEntry] = performance.getEntriesByType('navigation');
    if (navEntry?.type === 'reload' && !handledReload) {
      handledReload = true;
      window.history.scrollRestoration = 'manual';
      try {
        sessionStorage.removeItem('homeReturn');
      } catch {
        // Storage unavailable: nothing saved to clear.
      }
      if (window.location.hash) {
        navigate(window.location.pathname + window.location.search, { replace: true });
      }
      window.scrollTo(0, 0);
      return undefined;
    }

    let align = null;
    if (window.location.hash === '#contact') {
      align = () => contactRef.current?.scrollIntoView({ block: 'start' });
    } else if (window.location.hash === '#contact-form') {
      align = () => document.getElementById('contact-form')?.scrollIntoView({ block: 'start' });
    } else {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem('homeReturn'));
        sessionStorage.removeItem('homeReturn');
      } catch {
        saved = null;
      }
      if (saved) {
        align = () => {
          const el = saved.id ? document.getElementById(saved.id) : null;
          const base = el ? el.getBoundingClientRect().top + window.scrollY : 0;
          window.scrollTo(0, base + saved.offset);
        };
      }
    }
    if (!align) return undefined;

    align();
    const observer = new ResizeObserver(align);
    observer.observe(document.body);
    const stop = () => {
      observer.disconnect();
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    const timer = setTimeout(stop, 4000);
    return () => {
      clearTimeout(timer);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The mobile header's section label trails activeSection: the old label
  // fades out completely, is removed, and the header stays empty for a beat
  // before the new one fades in, so two labels are never on screen together.
  // Fast scrolling past several sections restarts the wait, and only the
  // section the visitor lands on is shown.
  useEffect(() => {
    if (activeSection === navLabel) {
      // Wait two frames so a freshly mounted label is painted at opacity 0
      // first; otherwise it would appear at full strength with no fade.
      let frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => setNavLabelVisible(true));
      });
      return () => cancelAnimationFrame(frame);
    }
    setNavLabelVisible(false);
    const timer = setTimeout(() => {
      setNavLabel(activeSection);
    }, NAV_LABEL_FADE_MS + NAV_LABEL_GAP_MS);
    return () => clearTimeout(timer);
  }, [activeSection, navLabel]);

  // Update active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100;

      for (const item of navItems) {
        if (item.ref.current) {
          const { offsetTop, offsetHeight } = item.ref.current;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(item.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Contact form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        form.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setSubmitMessage({
        type: 'success',
        text: 'Message sent! I\'ll get back to you as soon as possible.'
      });

      setFormData({
        name: '',
        email: '',
        message: ''
      });
    } catch (error) {
      setSubmitMessage({
        type: 'error',
        text: `Error: ${error.message || 'There was an error sending your message.'}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative bg-canvas text-ink">
      {/* Top Navigation - Desktop */}
      <nav className="hidden lg:block fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex items-center justify-between h-14">
            <button
              onClick={() => scrollToSection(homeRef)}
              className="text-[15px] font-bold tracking-tight text-ink"
            >
              Kacey Fillingim
            </button>

            <ul className="flex items-center gap-7">
              {navItems.filter(item => item.id !== 'home' && item.id !== 'contact').map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.ref)}
                    aria-current={activeSection === item.id ? 'true' : undefined}
                    className={`relative text-[13px] transition-colors duration-200 ${
                      activeSection === item.id
                        ? 'text-ink font-semibold'
                        : 'text-muted hover:text-ink'
                    }`}
                  >
                    {item.label}
                    <span
                      aria-hidden="true"
                      className={`absolute left-0 right-0 -bottom-1.5 h-[1.5px] rounded-full bg-ink origin-center transition-all duration-500 ease-out ${
                        activeSection === item.id ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-50'
                      }`}
                    />
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => scrollToSection(contactRef)}
                  className="rounded-full bg-ink px-4 py-1.5 text-[13px] font-medium text-white transition-colors duration-200 hover:bg-black"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className={`lg:hidden fixed top-0 left-0 right-0 z-40 bg-white/85 backdrop-blur-xl border-b border-black/5 transition-shadow duration-300 ${isMenuOpen ? 'shadow-[0_16px_32px_-8px_rgba(0,0,0,0.22)]' : ''}`}>
        <div className="px-5 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => scrollToSection(homeRef)}
              className="text-[15px] font-bold tracking-tight text-ink"
            >
              Kacey Fillingim
            </button>
            <div className="flex items-center gap-3">
              {/* Current section label. Keyed by section, so each label is a
                  new element and the previous one is gone from the page
                  before it appears. */}
              <div
                aria-hidden="true"
                className={`text-[13px] font-medium whitespace-nowrap transition-opacity duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}
              >
                <span
                  key={navLabel}
                  className={`block text-muted transition-opacity ease-out ${
                    navLabelVisible ? 'opacity-100' : 'opacity-0'
                  }`}
                  style={{ transitionDuration: `${NAV_LABEL_FADE_MS}ms` }}
                >
                  {navItems.find(item => item.id === navLabel && item.id !== 'home')?.label}
                </span>
              </div>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 -mr-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
              >
                <svg className="w-6 h-6 text-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 8h16M4 16h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
          {isMenuOpen && (
            <div className="mt-3 pb-3">
              <ul className="space-y-1">
                {navItems.filter(item => item.id !== 'home' && item.id !== 'contact').map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        scrollToSection(item.ref);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-[17px] transition-colors duration-200 ${
                        activeSection === item.id
                          ? 'bg-canvas text-ink font-semibold'
                          : 'text-muted hover:bg-canvas hover:text-ink'
                      }`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
                <li className="pt-3">
                  <button
                    onClick={() => {
                      scrollToSection(contactRef);
                      setIsMenuOpen(false);
                    }}
                    className="w-full rounded-full bg-ink px-5 py-3 text-[17px] font-medium text-white transition-colors duration-200 hover:bg-black"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-14 bg-canvas">
        {/* Hero */}
        <section ref={homeRef} id="home" className="bg-white">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 pt-20 pb-16 sm:pt-32 sm:pb-24">
            <p className="text-sm font-bold uppercase tracking-widest text-secondary">Kacey Fillingim</p>
            <h1 className="mt-4 text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight leading-[1.02] text-ink">
              Product designer
              <br />
              and founder.
            </h1>
            <p className="mt-8 max-w-2xl text-xl sm:text-2xl leading-snug text-muted">
              14+ years designing products from zero to one, across B2B SaaS, AI, and connected
              hardware.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => scrollToSection(aiProjectsRef)}
                className="rounded-full bg-ink px-7 py-3.5 text-[15px] font-medium text-white transition-colors duration-300 hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
              >
                View work
              </button>
              <button
                onClick={() => handleViewProtectedDocument('Resume', '/KaceyFillingim_resume.pdf')}
                className="rounded-full border border-ink/20 px-7 py-3.5 text-[15px] font-medium text-ink transition-colors duration-300 hover:border-ink/40 hover:bg-canvas focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
              >
                Resume
              </button>
            </div>

            {/* Proof row: every figure here comes from the project sections below */}
            <dl className="mt-20 sm:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10 border-t border-black/10 pt-10">
              {[
                ['14+ years', 'Designing products, from startup to enterprise'],
                ['$500K+ raised', 'As founder and CEO of Herba'],
                ['Acquired', 'Disco, by Culture Amp, as its sole designer'],
                ['Techstars', 'Founders Catalyst and OnDeck fellow'],
              ].map(([value, label]) => (
                <div key={value}>
                  <dt className="text-2xl sm:text-3xl font-bold tracking-tight text-secondary">{value}</dt>
                  <dd className="mt-2 text-[15px] leading-snug text-muted">{label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* AI Projects Section */}
        <section ref={aiProjectsRef} id="aiProjects" className="py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">
            <Reveal>
              <p className="text-sm font-bold uppercase tracking-widest text-secondary">AI Projects</p>
              <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-ink">
                Designed and built, end to end.
              </h2>
              {/* Same role, description, and bullets layout as the Experience cards below. */}
              <div className="max-w-2xl">
                <ProjectSummary
                  role="Independent Product Designer + Developer"
                  dates="2025 - Present"
                  description="Solo-built AI products, from idea to shipped, in weeks instead of months."
                  accomplishments={[
                                        'Conducted UX research to nail down a real problem before building, working closely with target users through development, testing, and launch',

                    'Self-taught development using existing APIs (Claude Code, Gemini Vision, OpenAI) and plugging them directly into real product experiences',
                    'Self-taught backend setups using MongoDB, Stripe, Clerk, and Resend to ship working infrastructure solo, not just design mockups',
                  ]}
                  accent="#4252a4"
                />
              </div>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 lg:auto-rows-fr gap-6">
              <Reveal className="h-full">
                <AIProjectCard
                  title="AllRooms.ai"
                  year="2026"
                  description="An AI collaboration platform for interior designers and their clients."
                  href="https://www.allrooms.ai/"
                  hero="/images/allrooms-hero.jpg"
                  heroAlt="AllRooms.ai project overview showing AI-generated visionboards for a living room and a patio"
                />
              </Reveal>
              <Reveal className="h-full">
                <AIProjectCard
                  title="Find Fun Now"
                  year="2025"
                  description="A platform for discovering fun activities and experiences in your area."
                  href="https://www.findfunnow.com"
                  hero="/images/findfun-hero.jpg"
                  heroAlt="Find Fun Now search results for San Francisco, with activity cards beside a map of their locations"
                />
              </Reveal>
              <Reveal className="h-full">
                <AIProjectCard
                  title="BoxyQR"
                  year="2025"
                  description="An AI-powered QR code generator with OCR for hardware products."
                  href="https://app.boxyqr.com"
                  hero="/images/boxyqr-hero.jpg"
                  heroAlt="BoxyQR's Create a QR Code screen with QR type options, a live blue QR code preview, and color and shape customization"
                />
              </Reveal>
              <Reveal className="h-full">
                <AIProjectCard
                  title="Coverly"
                  year="2026"
                  description="AI-generated cover letters, personalized for every job."
                  href="https://coverly.coach/"
                  hero="/images/coverly-hero.jpg"
                  heroAlt="Coverly's chat interview, where the AI asks follow-up questions about the user's experience"
                />
              </Reveal>
            </div>

            {/* VetVisitBill Card, hidden for now; flip SHOW_VETVISITBILL to bring it back. */}
            {SHOW_VETVISITBILL && (
              <div className="mt-6 mx-auto lg:w-[calc(50%-12px)]">
                <div className="bg-white rounded-2xl overflow-hidden flex flex-col p-5">
                  <ProductShot src="/images/vetvisit.png" alt="VetVisitBill" />
                  <h3 className="mt-5 text-lg font-semibold text-ink">VetVisitBill</h3>
                  <p className="text-sm text-muted">Created in 2026</p>
                  <p className="mt-2 text-muted">
                    All-in-one platform helping vets streamline medical records and billing, with
                    automated invoicing and predictive billing breakdowns.
                  </p>
                  <a href="https://www.vetvisitbill.com/" target="_blank" rel="noopener noreferrer" className="mt-4 text-sm font-medium text-secondary hover:underline">
                    View Website →
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Experience */}
        <div className="pb-24 sm:pb-32">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">
            <Reveal>
              <p className="text-sm font-bold uppercase tracking-widest text-secondary">Experience</p>
              <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-ink">
                Companies I've designed for.
              </h2>
            </Reveal>

            <div className="mt-14 space-y-6">
              {/* Herba Section */}
              <section ref={herbaRef} id="herba" className="scroll-mt-20">
                <Reveal>
                  <ProjectFeature
                    logo="/images/herba_logo_color.png"
                    logoAlt="Herba"
                    logoHeight={34}
                    role="Founder + CEO"
                    dates="Jun 2023 - Oct 2025"
                    description="Automated plant care smart enough to keep any space green, with zero effort."
                    accomplishments={[
                      'Raised $500K+ in pre-seed funding from industry leaders',
                      'Self-taught programming to develop custom firmware and web app',
                      'Selected for Techstars Founders Catalyst and OnDeck',
                      'Patented sensor technology',
                    ]}
                    accent="#00C853"
                    mediaBackground="#000000"
                    media={
                      <video
                        src="/images/herba.mp4"
                        aria-label="Herba solar-powered plant sensor rotating"
                        className="w-full max-w-md"
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    }
                    caseStudy={{ label: 'View case study', onClick: () => handleViewCaseStudy('Herba', '/work/herba') }}
                    website={{ label: 'Visit website ↗', href: 'https://herba.green' }}
                  />
                </Reveal>
              </section>

              {/* Culture Amp Section */}
              <section ref={cultureAmpRef} id="cultureAmp" className="scroll-mt-20">
                <Reveal>
                  <ProjectFeature
                    logo="/images/cultureamp_logo.png"
                    logoAlt="Culture Amp"
                    logoHeight={34}
                    role="Senior Product Designer"
                    dates="Oct 2021 - Jun 2023"
                    description="The platform behind some of the world's best company cultures."
                    accomplishments={[
                      'Led design of new recognition product, launching beta with 3 major enterprise customers',
                      'Facilitated cross-team brainstorming sessions and led design of a new workflow, driving a 60% increase in product usage and an 80% reduction in time to create a goal',
                      'Developed and presented accessibility framework to design and product org, partnering with engineering to implement WCAG standards',
                    ]}
                    accent="#A43FAF"
                    mediaBackground="#F4EDF8"
                    media={
                      <div className="w-full max-w-md rounded-xl overflow-hidden shadow-xl bg-white">
                        <div className="flex items-center gap-2 px-4 py-3 bg-gray-light" aria-hidden="true">
                          <span className="w-3 h-3 rounded-full bg-gray-300" />
                          <span className="w-3 h-3 rounded-full bg-gray-300" />
                          <span className="w-3 h-3 rounded-full bg-gray-300" />
                        </div>
                        <video
                          src="/images/cultureamp_video.mp4"
                          aria-label="Culture Amp goal creation: key results being added and formatted"
                          className="w-full h-auto block aspect-[668/594]"
                          autoPlay
                          loop
                          muted
                          playsInline
                        />
                      </div>
                    }
                    caseStudy={{ label: 'View case study', onClick: () => handleViewCaseStudy('Culture Amp', '/work/culture-amp') }}
                  />
                </Reveal>
              </section>

              {/* Disco Section */}
              <section ref={discoRef} id="disco" className="scroll-mt-20">
                <Reveal>
                  <ProjectFeature
                    logo="/images/disco_logo_color.png"
                    logoAlt="Disco"
                    logoHeight={40}
                    role="Senior Product Designer"
                    dates="Feb 2018 - Oct 2021"
                    description="Peer recognition that lives right inside Slack and Teams, no separate app required. (Acquired by Culture Amp),"
                    accomplishments={[
                      'Sole designer for 3.5 years, leading product design and roadmap strategy across product, sales, and engineering teams',
                      'Designed values nomination tool based on user research, securing largest client (300K employees) that influenced Culture Amp acquisition',
                    ]}
                    accent="#2A91B2"
                    mediaBackground="linear-gradient(160deg, #23AFC3 0%, #2B8EB0 100%)"
                    media={
                      <img
                        src="/images/disco1.gif"
                        alt="Disco values nomination flow inside Slack"
                        className="w-full max-w-md rounded-xl shadow-2xl"
                      />
                    }
                    caseStudy={{ label: 'View case study', onClick: () => handleViewCaseStudy('Disco', '/work/disco') }}
                  />
                </Reveal>
              </section>

              {/* Olapic Section */}
              <section ref={olapicRef} id="olapic" className="scroll-mt-20">
                <Reveal>
                  <ProjectFeature
                    logo="/images/olapic_logo.png"
                    logoAlt="Olapic"
                    logoHeight={40}
                    role="Senior Product Designer"
                    dates="Nov 2016 - Nov 2018"
                    description="Real customer photos and videos, turned into shoppable moments on every product page."
                    accomplishments={[
                      'Led data analytics redesign in collaboration with product and account management teams',
                      'Designed shoppable video experience, leading UX research and collaborating closely with stakeholders',
                    ]}
                    mediaBackground="#F2EEE9"
                    media={
                      <img
                        src="/images/olapic1.gif"
                        alt="Olapic shoppable UGC widget cycling through customer posts and videos"
                        className="w-full max-w-[260px] rounded-2xl shadow-xl"
                      />
                    }
                  />
                </Reveal>
              </section>

              {/* Flok Section */}
              <section ref={flokRef} id="flok" className="scroll-mt-20">
                <Reveal>
                  <ProjectFeature
                    logo="/images/flok_logo.png"
                    logoAlt="Flok"
                    logoHeight={40}
                    role="Senior Product Designer"
                    dates="Mar 2016 - Nov 2016"
                    description="The paper punch card, reinvented: automatic, beacon-powered loyalty that runs itself."
                    accomplishments={[
                      'Designed the merchant sign-in, onboarding, and analytics experiences for Flok\'s beacon-powered loyalty app, from visual design through interaction',                     
                      'Collaborated with other designers and a remote development team to ship reward campaigns and customer messaging',
                    ]}
                    accent="#00A0E0"
                    mediaBackground="#39444A"
                    media={
                      <img
                        src="/images/case-studies/flok/splash-signin.gif"
                        alt="Flok business app intro animation"
                        className="w-full max-w-[240px] rounded-lg shadow-2xl"
                      />
                    }
                    caseStudy={{ label: 'View case study', onClick: () => handleViewCaseStudy('Flok', '/work/flok') }}
                  />
                </Reveal>
              </section>

              {/* WhoSay Section */}
              <section ref={whosayRef} id="whosay" className="scroll-mt-20">
                <Reveal>
                  <ProjectFeature
                    logo="/images/whosay_logo.png"
                    logoAlt="WhoSay"
                    logoHeight={26}
                    role="Senior Product Designer"
                    dates="Jun 2014 - Mar 2016"
                    description="The original all-in-one platform for influencer marketing: one post, every platform, millions of fans reached."
                    accomplishments={[
                      'Sole designer for the Talent app (iOS and Android), the publishing tool celebrities and their teams used to reach millions of fans across every platform at once',
                      "Designed Match Report, an affinity engine matching brands with the celebrities whose audience actually fit, powering the company's pivot into social brand deals",
                    ]}
                    accent="#0086C5"
                    mediaBackground="#E6F4FB"
                    media={
                      <img
                        src="/images/case-studies/whosay/my-posts.webp"
                        alt="WhoSay Talent app My Posts screen on Android: 15,804,290 total fans, an On Fire past-7-days summary, and top posts by engagement"
                        className="w-full max-w-[240px]"
                      />
                    }
                    caseStudy={{ label: 'View case study', onClick: () => handleViewCaseStudy('WhoSay', '/work/whosay') }}
                  />
                </Reveal>
              </section>
            </div>
          </div>
        </div>

        {/* Other Work Section */}
        <section ref={otherWorkRef} id="otherWork" className="bg-white py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">
            <Reveal>
              <p className="text-sm font-bold uppercase tracking-widest text-secondary">Earlier work</p>
              <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-ink">
                Older and personal work.
              </h2>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Saks Card */}
              <Reveal className="h-full">
                <div className="h-full rounded-[28px] bg-canvas p-8 sm:p-10">
                  <img src="/images/saks.png" alt="Saks Fifth Avenue" className="h-10 object-contain object-left mix-blend-multiply" />
                  <p className="mt-8 text-sm font-medium text-muted">
                    <span className="font-bold">Junior UX Designer</span><span className="mx-2" aria-hidden="true">·</span>Jun 2013 - Nov 2014
                  </p>
                  <p className="mt-3 text-xl leading-snug font-medium tracking-tight text-ink">
                    One of the most iconic names in American luxury retail.
                  </p>
                  <ul className="mt-6 space-y-3">
                    {[
                      'Redesigned and launched mobile app visuals in collaboration with editorial team',
                      'Updated marketing email designs',
                      'Designed a UGC (user-generated content) shoppable experience, collaborating directly with outside development vendors',
                    ].map((item) => (
                      <li key={item} className="flex gap-3 text-[15px] leading-relaxed text-muted">
                        <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-ink" aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              {/* Previous Card */}
              <Reveal className="h-full">
                <div className="h-full rounded-[28px] bg-canvas p-8 sm:p-10">
                  <p className="text-2xl font-bold tracking-tight text-ink">Previous</p>
                  <p className="mt-8 text-sm font-medium text-muted">
                    <span className="font-bold">Freelance Product Designer</span><span className="mx-2" aria-hidden="true">·</span>June 2012 - May 2013
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-10">
                    <img src="/images/conde_nast_logo_trim.png" alt="Condé Nast" className="h-7 sm:h-8 w-auto object-contain mix-blend-multiply" />
                    <img src="/images/quidsi_logo_trim.png" alt="Quidsi" className="h-12 sm:h-14 w-auto object-contain mix-blend-multiply" />
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Saks Images */}
            <Reveal className="mt-24">
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink">Saks Fifth Avenue work samples</h3>
              <ul className="mt-4 flex flex-wrap gap-2" aria-label="Skills">
                {['UX/UI', 'Visual Design', 'Typography'].map((tag) => (
                  <li key={tag} className="rounded-full bg-canvas px-3 py-1 text-[13px] font-medium text-muted">{tag}</li>
                ))}
              </ul>
            </Reveal>

            <Reveal>
              <figure className="mt-10">
                <div className="rounded-[28px] bg-canvas p-6 sm:p-12">
                  <img
                    src="/images/saks-saksstyle.webp"
                    alt="#SaksStyle on two phones and a desktop: a landing screen inviting shoppers to tag their Saks looks on Instagram or Facebook, a feed of tagged posts with Shop Now product tags, and the same shoppable grid on Saks.com"
                    className="w-full"
                  />
                </div>
                <figcaption className="text-sm text-muted mt-4 max-w-3xl">
                  <span className="font-semibold text-ink mr-1.5">#SaksStyle</span>
                  Designed a digital experience allowing users to tag Instagram content, which would create shoppable UGC for the website
                </figcaption>
              </figure>
            </Reveal>

            <Reveal>
              <figure className="mt-16">
                {/* On phones, the image's own blank side margins would leave the
                    emails narrower than the art below, so scale past them. */}
                <div className="rounded-[28px] bg-canvas p-6 sm:p-12 overflow-hidden">
                  <img
                    src="/images/saks1.png"
                    alt="Four Saks emails: a Don't Forget abandoned cart email, a Tell Us What You Think post-purchase review email, a The Wait Is Over wait list email, and a Just for You recommendation email"
                    className="max-w-none w-[118%] -ml-[8.4%] sm:w-full sm:ml-0 mix-blend-multiply"
                  />
                </div>
                <figcaption className="text-sm text-muted mt-4 max-w-3xl">
                  <span className="font-semibold text-ink mr-1.5">Transactional emails</span>
                  Designed the typography and layouts for the abandoned cart, post-purchase review, wait list, and custom recommendation emails, and partnered with email engineering to build them as automated HTML templates.
                </figcaption>
              </figure>
            </Reveal>

            {/* Personal Work */}
            <Reveal className="mt-24">
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink">Personal work</h3>
              <ul className="mt-4 flex flex-wrap gap-2" aria-label="Skills">
                {['UX/UI', 'Abstract Art', 'Visual Design'].map((tag) => (
                  <li key={tag} className="rounded-full bg-canvas px-3 py-1 text-[13px] font-medium text-muted">{tag}</li>
                ))}
              </ul>
            </Reveal>

            <Reveal>
              <figure className="mt-10">
                {/* Same as the emails: scale past the image's blank side margins on phones. */}
                <div className="rounded-[28px] bg-canvas p-6 sm:p-12 overflow-hidden">
                  <img
                    src="/images/RS.webp"
                    alt="Rolling Stone iPad app designs"
                    className="max-w-none w-[137%] -ml-[18.7%] sm:w-full sm:ml-0 mix-blend-multiply"
                  />
                </div>
                <figcaption className="text-sm text-muted mt-4 max-w-3xl">
                  <span className="font-semibold text-ink mr-1.5">Rolling Stone iPad App</span>
                  A school project in collaboration with Mun May Tee. Won a SCADdy Gold award in 2013 for visual design.
                </figcaption>
              </figure>
            </Reveal>

            {/* Abstract art */}
            <Reveal>
              <figure className="mt-16">
                <div className="rounded-[28px] bg-canvas p-6 sm:p-12">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                    {['art1', 'art2', 'art3'].map((name, i) => (
                      <img
                        key={name}
                        src={`/images/${name}.webp`}
                        alt={`Abstract artwork ${i + 1} by Kacey Fillingim`}
                        className="w-full h-auto rounded-2xl"
                      />
                    ))}
                  </div>
                </div>
                <figcaption className="text-sm text-muted mt-4 max-w-3xl">
                  <span className="font-semibold text-ink mr-1.5">Abstract art</span>
                  My personal abstract artwork, created outside of product design work
                  <span className="mx-3 text-muted" aria-hidden="true">|</span>
                  <a href="https://kaceyaf.art/" target="_blank" rel="noopener noreferrer" className="font-medium text-secondary hover:underline">View Art</a>
                </figcaption>
              </figure>
            </Reveal>
          </div>
        </section>

        {/* Contact Section */}
        <section ref={contactRef} id="contact" className="py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6 sm:px-8">
            <Reveal>
              <p className="text-sm font-bold uppercase tracking-widest text-secondary">Contact</p>
              <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-ink">
                Let's work together.
              </h2>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-6">
              <Reveal>
                <div className="rounded-[28px] bg-white p-8 sm:p-10 space-y-8">
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest text-tertiary">Email</h3>
                    <p className="mt-2 text-lg text-tertiary">kfilli20@gmail.com</p>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest text-tertiary">Location</h3>
                    <p className="mt-2 text-lg text-tertiary">San Francisco, CA</p>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest text-tertiary">See more</h3>
                    <p className="mt-2 flex items-center text-sm">
                      <button onClick={() => handleViewProtectedDocument('Resume', '/KaceyFillingim_resume.pdf')} className="font-medium text-secondary hover:underline">View Resume</button>
                      <span className="mx-3 text-muted" aria-hidden="true">|</span>
                      <a href="https://kaceyaf.art/" target="_blank" rel="noopener noreferrer" className="font-medium text-secondary hover:underline">View Art</a>
                    </p>
                  </div>
                </div>
              </Reveal>

              <Reveal>
                <div id="contact-form" className="rounded-[28px] bg-white p-8 sm:p-10 scroll-mt-24">
                  <h3 className="text-2xl font-bold tracking-tight text-ink">Send a message</h3>
                  <p className="mt-1 text-sm text-muted">All fields are required.</p>

                  <form ref={form} onSubmit={handleSubmit} className="mt-8">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-xs font-bold uppercase tracking-widest text-tertiary mb-2">Your name</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          autoComplete="name"
                          placeholder="Jane Smith"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-canvas border border-gray-500 rounded-xl text-ink placeholder:text-gray-400 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-widest text-tertiary mb-2">Your email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          autoComplete="email"
                          placeholder="jane@company.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-canvas border border-gray-500 rounded-xl text-ink placeholder:text-gray-400 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label htmlFor="message" className="block text-xs font-bold uppercase tracking-widest text-tertiary mb-2">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        placeholder="Hi Kacey, I'd love to talk about..."
                        className="w-full px-4 py-3 bg-canvas border border-gray-500 rounded-xl text-ink placeholder:text-gray-400 focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="mt-6 w-full sm:w-auto rounded-full bg-ink px-8 py-3.5 text-[15px] font-medium text-white transition-colors duration-300 hover:bg-black disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2"
                    >
                      {isSubmitting ? 'Sending...' : 'Send message'}
                    </button>

                    {/* Kept mounted so screen readers announce the result when it appears */}
                    <div role="status" aria-live="polite" aria-atomic="true">
                      {submitMessage && (
                        <div className={`mt-4 p-3 rounded-xl ${submitMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                          {submitMessage.text}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <Footer />
      </main>

      {/* Password Modal */}
      <PasswordModal
        isOpen={passwordModal.isOpen}
        onClose={() => setPasswordModal({ isOpen: false, projectName: '', pdfUrl: '' })}
        onSuccess={handlePasswordSuccess}
        projectName={passwordModal.projectName}
        onContactClick={handleContactScroll}
      />
    </div>
  );
}

export default App;